import { Box, Skeleton, Stack, Typography } from "@mui/material";

import { DayOfWeekLong } from "@app/common/types";
import { getSemanticScore } from "@app/common/utils";

import ModuleLegendItem from "../components/ModuleLegendItem";

const LABELS = [
  "6am - 9am",
  "9am - 12pm",
  "12pm - 3pm",
  "3pm - 6pm",
  "6pm - 9pm",
];

const getBackgroundColor = (avgScore: number | null) => {
  const semanticScore = getSemanticScore(avgScore);
  switch (semanticScore) {
    case "positive":
      return "scores.positive";
    case "neutral":
      return "shades.neutral.50";
    case "negative":
      return "scores.negative";
    default:
      return "white";
  }
};

type DataBlock = { block: number; avgScore: number | null };

export interface DayAndTimeChartProps {
  data: { [k in DayOfWeekLong]: DataBlock[] };
  loading: boolean;
}

const Column = ({
  day,
  data,
  loading,
}: {
  day: DayOfWeekLong;
  data: DayAndTimeChartProps["data"];
  loading: boolean;
}) => {
  const dataForDay = data[day];

  return (
    <Stack key={day} spacing={0.25} alignItems="center" sx={{ width: "100%" }}>
      <Typography variant="labelMed">{day.charAt(0).toUpperCase()}</Typography>
      {dataForDay.map((block: DataBlock) => {
        if (loading) {
          return (
            <Skeleton
              key={block.block}
              variant="rectangular"
              sx={{ height: "26px", width: "100%", borderRadius: "4px" }}
            />
          );
        }

        const blockColor = getBackgroundColor(block.avgScore);
        return (
          <Box
            key={block.block}
            sx={{
              height: "26px",
              width: "100%",
              border: "solid 1px",
              borderColor: block.avgScore ? blockColor : "shades.neutral.50",
              bgcolor: blockColor,
              borderRadius: "4px",
            }}
          />
        );
      })}
    </Stack>
  );
};

const DayAndTimeChart = ({ data, loading }: DayAndTimeChartProps) => {
  const weekDays = Object.keys(data) as (keyof NonNullable<
    DayAndTimeChartProps["data"]
  >)[];

  return (
    <Stack>
      <Stack direction="row" spacing={2} sx={{ py: 1 }}>
        <Typography
          variant="bodySecondaryReg"
          sx={{ color: "shades.neutral.500" }}
        >
          Engagement:
        </Typography>
        <ModuleLegendItem label="Positive" color="shades.meadow.500" />
        <ModuleLegendItem label="Neutral" color="shades.neutral.100" />
        <ModuleLegendItem label="Negative" color="shades.negative.500" />
        <ModuleLegendItem label="No Data" color="shades.neutral.150" outline />
      </Stack>
      <Stack sx={{ width: "100%" }}>
        <Stack direction="row" sx={{ width: "100%", mt: 2 }} spacing={0.5}>
          <Stack
            justifyContent="space-between"
            sx={{
              pt: "24px",
              width: "80px",
              color: "shades.neutral.400",
              mr: 1,
            }}
          >
            {LABELS.map((label) => (
              <Typography
                key={label}
                variant="labelMed"
                sx={{ height: "22px" }}
              >
                {label}
              </Typography>
            ))}
          </Stack>
          <Stack
            direction="row"
            sx={{
              width: "calc(100% - 80px)", // 80px = width of labels (above)
            }}
            spacing={0.25}
          >
            {weekDays.map((day) => (
              <Column key={day} day={day} data={data} loading={loading} />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default DayAndTimeChart;
